// مسیر فایل: src/utils/referral.js
// وظیفه: خواندن و نگه‌داشتن کد معرف تا لحظهٔ ثبت‌نام
//
// The agent's code, from the link to the account.
//
// An agent's invite link was built in two places and read in none:
//
//   * the player app's agent dashboard got `${domain}/join?ref=CODE` from
//     `/api/wallet/referral`, and the site has no `/join` route - so the link
//     an agent actually hands out went straight to the 404 page;
//   * the admin panel built `${domain}/?ref=CODE` for the same agent, which at
//     least lands somewhere, but nothing on the landing page or the sign-up
//     form ever looked at `ref`, so the code was dropped and the account was
//     created with no agent behind it.
//
// The register API has accepted `referralCode` all along. It was the only part
// of the chain that worked.
//
// The code is remembered rather than read once, because arriving and signing up
// are not the same visit: somebody follows the link, reads the landing page,
// comes back the next day and registers. A code held only in the URL of the
// first page is a commission nobody gets paid.

/// Where the code waits between arriving and signing up.
const STORAGE_KEY = 'shahpoker.referral';

/// How long a remembered code is honoured.
///
/// Long enough to cover thinking about it for a few days, short enough that a
/// code picked up months ago is not silently attached to an account the agent
/// had nothing to do with.
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

/// Codes are generated by the backend as short alphanumerics. Anything else in
/// the query string is somebody else's business, and is not stored.
const CODE_PATTERN = /^[A-Za-z0-9_-]{3,32}$/;

/** The parameter names an invite link has used, in the order they are tried. */
const PARAMS = ['ref', 'referral', 'referralCode', 'invite'];

function readStorage() {
    try {
        return window.localStorage;
    } catch {
        // Private browsing, or a browser set to block site data. The code then
        // works for this page load and no further, which is the same as it
        // behaving like a plain query parameter.
        return null;
    }
}

/**
 * Picks the code out of a query string, if there is one.
 *
 * @param {string} search `window.location.search`
 * @returns {string|null}
 */
export function codeFromSearch(search) {
    let params;
    try {
        params = new URLSearchParams(search || '');
    } catch {
        return null;
    }

    for (const name of PARAMS) {
        const raw = (params.get(name) || '').trim();
        if (raw && CODE_PATTERN.test(raw)) return raw;
    }
    return null;
}

/**
 * Remembers a code for later.
 *
 * Called on every page load, so a visitor who arrives on the landing page and
 * signs up two screens later still carries it.
 */
export function rememberReferral(search) {
    const code = codeFromSearch(search);
    if (!code) return null;

    const store = readStorage();
    if (store) {
        try {
            store.setItem(
                STORAGE_KEY,
                JSON.stringify({ code, at: Date.now() }),
            );
        } catch {
            // Out of quota, or blocked. Nothing to do about it, and not worth
            // failing a page load over.
        }
    }
    return code;
}

/** The code to attach to a sign-up, or null. */
export function currentReferral() {
    const store = readStorage();
    if (!store) return null;

    let raw;
    try {
        raw = store.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
    if (!raw) return null;

    let saved;
    try {
        saved = JSON.parse(raw);
    } catch {
        // Written by an older build, or by hand. Drop it rather than guess.
        clearReferral();
        return null;
    }

    if (!saved || !CODE_PATTERN.test(String(saved.code || ''))) {
        clearReferral();
        return null;
    }

    if (!saved.at || Date.now() - saved.at > TTL_MS) {
        clearReferral();
        return null;
    }

    return saved.code;
}

/** Forgets the code. Called once an account has been made with it. */
export function clearReferral() {
    const store = readStorage();
    if (!store) return;
    try {
        store.removeItem(STORAGE_KEY);
    } catch {
        // Same as above.
    }
}
